import { toSdkLoggableError } from '../../../types/shared';
import { trackValidationError } from '../../telemetry/pageTelemetry';
import { logScrollWarning } from '../../logger/navigationLogger';
import { logValidationErrorPage, logValidationFailedSubmit } from '../../logger/validationLogger';
import { applyMcqDefaults } from '../../utils/buildInitialAnswers';
import { findQuestionElement, validateQuestions } from '../../utils';
import type { OnNextRunState } from './types';

/**
 * Validate visible questions on the current page.
 * Returns true when validation passed and navigation can continue.
 */
export function validateCurrentPage(run: OnNextRunState): boolean {
  const answersForValidation = applyMcqDefaults(run.pages, run.values);
  if (answersForValidation !== run.values) {
    run.setValues(answersForValidation);
  }

  let errors: Record<string, string> = {};
  try {
    errors = validateQuestions(
      run.currentQuestions,
      answersForValidation,
      run.survey.language,
      run.allQuestions,
      run.logicContext.customFieldValues ?? {}
    );
  } catch (err) {
    logValidationFailedSubmit(toSdkLoggableError(err));
  }

  if (Object.keys(errors).length === 0) {
    run.setValidationErrors({});
    delete run.refs.validationAttempt[run.currentPageIndex];
    return true;
  }

  logValidationErrorPage(errors);

  const attempt = (run.refs.validationAttempt[run.currentPageIndex] || 0) + 1;
  run.refs.validationAttempt[run.currentPageIndex] = attempt;
  run.refs.validationErrorsCount += 1;
  run.refs.validationHistory.push({
    pageIndex: run.currentPageIndex,
    errors,
    attemptNumber: attempt,
  });

  trackValidationError(run.currentPageIndex, errors, attempt);
  run.setValidationErrors(errors);

  const firstErrorId = Object.keys(errors)[0];
  setTimeout(() => {
    try {
      const element = findQuestionElement(firstErrorId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (err) {
      logScrollWarning(
        'Failed to scroll validation error element into view',
        toSdkLoggableError(err)
      );
    }
  }, 50);

  return false;
}
